import { CompoundInput } from '@evisort/eds/components/CompoundInput/CompoundInput';
import { defineMessages, useIntl } from 'react-intl';

import type { Input } from '~/eds/src/types/Input';

export type DurationPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type DurationValue = { amount: number | null; period: DurationPeriod };

const DEFAULT_PERIOD: DurationPeriod = 'day';

export const EMPTY_DURATION_VALUE: DurationValue = {
  amount: null,
  period: DEFAULT_PERIOD,
};

// Mirrors Pilot's `DurationStrategy._ALLOWED_PERIODS`. Exported so CSV import
// (fieldManagerUtils.ts) can derive its singular/plural period tokens from
// the same list instead of hand-copying it.
export const PERIODS: DurationPeriod[] = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
];

const messages = defineMessages({
  groupLabel: {
    id: 'DurationInput.groupLabel',
    defaultMessage: 'Amount and period',
    description: 'Accessible name for the amount-and-period field',
  },
  amountLabel: {
    id: 'DurationInput.amountLabel',
    defaultMessage: 'Amount',
    description: 'Accessible name for the duration amount input',
  },
  amountPlaceholder: {
    id: 'DurationInput.amountPlaceholder',
    defaultMessage: 'Enter amount',
    description: 'Placeholder for the duration amount input',
  },
  periodLabel: {
    id: 'DurationInput.periodLabel',
    defaultMessage: 'Duration period',
    description: 'Accessible name for the duration period select',
  },
  day: {
    id: 'DurationInput.period.day',
    defaultMessage: '{count, plural, one {Day} other {Days}}',
    description: 'Duration period option: day(s)',
  },
  week: {
    id: 'DurationInput.period.week',
    defaultMessage: '{count, plural, one {Week} other {Weeks}}',
    description: 'Duration period option: week(s)',
  },
  month: {
    id: 'DurationInput.period.month',
    defaultMessage: '{count, plural, one {Month} other {Months}}',
    description: 'Duration period option: month(s)',
  },
  quarter: {
    id: 'DurationInput.period.quarter',
    defaultMessage: '{count, plural, one {Quarter} other {Quarters}}',
    description: 'Duration period option: quarter(s)',
  },
  year: {
    id: 'DurationInput.period.year',
    defaultMessage: '{count, plural, one {Year} other {Years}}',
    description: 'Duration period option: year(s)',
  },
});

export function DurationInput({
  name,
  value,
  onChange,
  ...rest
}: Input<DurationValue>) {
  const intl = useIntl();
  const amount = value?.amount ?? null;
  const period = value?.period ?? DEFAULT_PERIOD;

  const options = PERIODS.map((p) => ({
    id: p,
    value: p,
    text: intl.formatMessage(messages[p], { count: amount ?? 1 }),
  }));

  return (
    <CompoundInput<DurationValue>
      __private={
        rest.id
          ? { ariaLabelledBy: `${rest.id}-label`, id: rest.id }
          : undefined
      }
      disabled={rest.disabled}
      label={intl.formatMessage(messages.groupLabel)}
      name={name}
      onKeyDown={rest.onKeyDown}
      segments={[
        {
          label: intl.formatMessage(messages.amountLabel),
          name: 'amount',
          placeholder: intl.formatMessage(messages.amountPlaceholder),
          type: 'number',
          width: 'fill',
        },
        {
          label: intl.formatMessage(messages.periodLabel),
          name: 'period',
          options,
          type: 'select',
          width: 'hug',
        },
      ]}
      status={rest.error ? 'critical' : undefined}
      value={{ amount, period }}
      onChange={(next) =>
        onChange({
          amount: next.amount,
          period: next.period ?? DEFAULT_PERIOD,
        })
      }
    />
  );
}
